import { useEffect, useRef } from 'react';
import { useSetState } from 'react-use';
import dayjs from 'dayjs';
// Api
import { useLazyQuery } from '@apollo/client';
import { GetSupportedOtpCountries } from 'api/otp/types/GetSupportedOtpCountries';
import { GET_SUPPORTED_OTP_COUNTRIES } from 'api/otp/queries';
// Helpers
import {
  detectCountryFromPhone,
  checkIsCountrySupported,
  maskPhone,
} from 'helpers/phoneUtils';
// Hooks
import { useOnboardingContext } from 'hooks';
import {
  useSmsOtpVerification,
  useEmailOtpVerification,
} from 'hooks/useOtpVerification';
// Ui3
import Modal from 'ui3/Modal/Modal';
import Loader from 'ui3/Loader/Loader';
// Components
import { showToast } from 'components/common/Toast/Toast';
import OTPForm, { OTPFormData } from './components/OTPForm/OTPForm';
import EmailForm from './components/EmailForm/EmailForm';
// Styles
import styles from './OTPModal.module.scss';
import { OTPType } from 'context/OnboardingContext';

const maskEmail = (email: string): string => {
  if (!email) return '';

  const [localPart, domain] = email.split('@');
  if (!domain) return email;

  // For short local parts (3 or fewer chars), show at least 1 char
  if (localPart.length <= 3) {
    // Show first character and mask the rest (if any)
    const maskedLocalPart =
      localPart.slice(0, 1) +
      (localPart.length > 1 ? '*'.repeat(localPart.length - 1) : '');
    return `${maskedLocalPart}@${domain}`;
  }

  // Show first 3 characters of local part, mask the rest
  const maskedLocalPart =
    localPart.slice(0, 3) + '*'.repeat(Math.max(localPart.length - 3, 1));

  return `${maskedLocalPart}@${domain}`;
};

const formatContactDisplay = (isPhone: boolean, value?: string) => {
  if (!value) return '';

  if (isPhone) {
    return maskPhone(value);
  } else {
    return maskEmail(value);
  }
};

type Step = 'phone-otp' | 'email-form' | 'email-otp';

type DefaultState = {
  currentStep: Step;
  isCountrySupported: boolean;
  manuallySendPhoneCode: boolean;
  customEmail: string;
  firstPhoneInput: string;
};

type OTPModalProps = {
  phoneValue: string;
  emailValue: string;
  initialStep?: Step;
  updateManualEmailInput?: (email: string) => void;
};

const defaultState: DefaultState = {
  currentStep: 'phone-otp',
  isCountrySupported: true,
  manuallySendPhoneCode: false,
  customEmail: '',
  firstPhoneInput: '',
};

const OTPModal = ({
  phoneValue,
  emailValue,
  updateManualEmailInput,
  initialStep,
}: OTPModalProps) => {
  const [state, setState] = useSetState<DefaultState>({
    ...defaultState,
    currentStep: initialStep || defaultState.currentStep,
  });
  const previousStep = useRef<Step>('phone-otp');
  const {
    showOTPModal,
    setShowOTPModal,
    setOTPValidation,
    otpValidation,
    lastOtpSentTimes,
    setLastOtpSentTime,
  } = useOnboardingContext();
  const manualEmailInput =
    otpValidation?.type === 'social' && !otpValidation?.hasEmail;
  const countryCode = detectCountryFromPhone(phoneValue || '');
  const timingType = state.currentStep === 'phone-otp' ? 'phone' : 'email';

  const {
    sendVerification: sendSmsVerification,
    verifyCode: verifySmsCode,
    hasReferenceId: hasSmsReferenceId,
  } = useSmsOtpVerification(phoneValue || '');
  const {
    sendVerification: sendEmailVerification,
    verifyCode: verifyEmailCode,
  } = useEmailOtpVerification(emailValue || '');

  const isPhoneStep = state.currentStep === 'phone-otp';
  const isEmailFormStep = state.currentStep === 'email-form';
  const isEmailOtpStep = state.currentStep === 'email-otp';

  const getModalTitle = () => {
    if (isEmailFormStep) return 'Verify With E-Mail';
    return 'Enter Code';
  };

  const getDescription = () => {
    if (isPhoneStep) {
      if (state.manuallySendPhoneCode) {
        // It would need manual trigger for sending the code
        return "We'll send an SMS with an activation code to your phone";
      }
      return "We've sent an SMS with an activation code to your phone";
    }
    return 'Please enter the verification code';
  };

  const [
    fetchSupportedSMSCountries,
    { data: supportedOTPCountries, loading: isLoadingSupportedOTPCountries },
  ] = useLazyQuery<GetSupportedOtpCountries>(GET_SUPPORTED_OTP_COUNTRIES);

  // This mustn't be memoized as timing will change when screens are changed
  const checkOtpTiming = (type: 'phone' | 'email') => {
    const now = dayjs();
    const oneMinuteAgo = now.subtract(1, 'minute');
    const lastSentTime = lastOtpSentTimes?.[type];

    // If it has initial step, email/phone number will be different, so we can send the code
    if (lastSentTime && lastSentTime.isAfter(oneMinuteAgo) && !initialStep) {
      const timeLeft = lastSentTime.diff(oneMinuteAgo, 'second');
      return { canSend: false, timeLeft };
    }
    return { canSend: true, timeLeft: 0 };
  };
  const { timeLeft } = checkOtpTiming(timingType);

  const handleOnClose = () => {
    // If we're in email-otp, go back to email-form
    if (isEmailOtpStep) {
      setState({ currentStep: 'email-form' });
      return;
    }

    // If we're in email-form and country is supported, go back to phone step
    if (
      isEmailFormStep &&
      state.isCountrySupported &&
      initialStep !== 'email-form'
    ) {
      setState({ currentStep: 'phone-otp', manuallySendPhoneCode: true });
      return;
    }

    setShowOTPModal(false);
    setState(defaultState);
  };

  const handleFormSubmit = async (values: OTPFormData) => {
    try {
      const result =
        state.currentStep === 'phone-otp'
          ? await verifySmsCode(values.otpCode)
          : state.customEmail
          ? await verifyEmailCode(values.otpCode, state.customEmail)
          : await verifyEmailCode(values.otpCode);

      if (result.isValid && result.verificationId) {
        setOTPValidation({
          type:
            state.currentStep === 'phone-otp' ? OTPType.Phone : OTPType.Email,
          id: result.verificationId,
        });
        // Update parent form with email if it's a social signup without email
        if (state.customEmail && otpValidation?.type === 'social') {
          updateManualEmailInput?.(state.customEmail);
        }
        setShowOTPModal(false);
      }
    } catch (error) {
      showToast({
        message: 'There was an error submitting your code',
        type: 'error',
      });
    }
  };

  const handleResendCode = async () => {
    const { canSend } = checkOtpTiming(timingType);
    if (!canSend) return;

    if (state.currentStep === 'phone-otp') {
      await sendSmsVerification();
      setLastOtpSentTime('phone', dayjs());
      return;
    } else if (state.customEmail) {
      await sendEmailVerification(state.customEmail);
    } else {
      await sendEmailVerification();
    }
    setLastOtpSentTime('email', dayjs());
  };

  const handleSendCode = (email?: string) => {
    const { canSend } = checkOtpTiming('email');
    if (!canSend) return;

    sendEmailVerification(email);
    setLastOtpSentTime('email', dayjs());
    previousStep.current = state.currentStep;
    setState({ currentStep: 'email-otp' });
  };

  const handleVerifyAnotherWay = () => {
    previousStep.current = state.currentStep;
    setState({ currentStep: 'email-form', manuallySendPhoneCode: true });
  };

  const handleCustomEmail = (email: string) => {
    setState({ customEmail: email });
  };

  // Get supported countries for SMS verification and send SMS if supported
  useEffect(() => {
    if (!showOTPModal) {
      return;
    }

    const checkAndSendSMS = async () => {
      if (!supportedOTPCountries) {
        await fetchSupportedSMSCountries();
        return;
      }

      const isCountrySupported = checkIsCountrySupported(
        supportedOTPCountries.getSupportedOtpCountries,
        countryCode
      );
      setState({ isCountrySupported });

      if (
        (!isCountrySupported && state.currentStep !== 'email-form') ||
        initialStep === 'email-form'
      ) {
        previousStep.current = 'email-form';
        setState({ currentStep: 'email-form' });
        if ((!initialStep && phoneValue) || initialStep === 'phone-otp') {
          showToast({
            message: 'Country not supported for SMS verification',
            type: 'info',
          });
        }
        return;
      }

      // Only send SMS if we're in phone step and haven't sent it yet
      if (state.currentStep === 'phone-otp' && !hasSmsReferenceId) {
        const { canSend } = checkOtpTiming('phone');
        if (!canSend) return;

        await sendSmsVerification();
        setLastOtpSentTime('phone', dayjs());
      }
    };

    checkAndSendSMS();
    // Don't rerun this effect when step changes and lastOtpSentTimes changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryCode,
    fetchSupportedSMSCountries,
    supportedOTPCountries,
    setState,
    showOTPModal,
    hasSmsReferenceId,
    sendSmsVerification,
    setLastOtpSentTime,
  ]);

  if (isLoadingSupportedOTPCountries) {
    return (
      <Modal
        size="small"
        closeIconPosition="left"
        closeIcon="arrowLeft"
        title="Verify"
        open={showOTPModal}
        onClose={handleOnClose}
      >
        <>
          <div className={styles.placeholder}></div>
          <Loader loading size="large" className={styles.loader} />
        </>
      </Modal>
    );
  }

  return (
    <Modal
      size="small"
      closeIconPosition="left"
      closeIcon="arrowLeft"
      title={getModalTitle()}
      open={showOTPModal}
      onClose={handleOnClose}
    >
      <>
        {isPhoneStep && (
          <OTPForm
            description={getDescription()}
            onSubmit={handleFormSubmit}
            {...(!initialStep
              ? { onVerifyAnotherWay: handleVerifyAnotherWay }
              : {})}
            contactType="phone"
            contactValue={formatContactDisplay(true, phoneValue)}
            manuallySendCode={state.manuallySendPhoneCode}
            onResendCode={handleResendCode}
            initialTimeLeft={timeLeft}
          />
        )}

        {isEmailFormStep && (
          <EmailForm
            email={formatContactDisplay(false, emailValue)}
            onSendCode={handleSendCode}
            manualInput={manualEmailInput}
            setCustomEmail={handleCustomEmail}
            initialTimeLeft={timeLeft}
          />
        )}

        {isEmailOtpStep && (
          <OTPForm
            description={getDescription()}
            onSubmit={handleFormSubmit}
            contactType="email"
            onResendCode={handleResendCode}
            {...(manualEmailInput
              ? { contactValue: formatContactDisplay(false, state.customEmail) }
              : {})}
            initialTimeLeft={timeLeft}
          />
        )}
      </>
    </Modal>
  );
};

export default OTPModal;
